// =============================================================================
// cognitoAuth.ts
// -----------------------------------------------------------------------------
// Thin wrapper around AWS Cognito (User Pool + App Client) using the lightweight
// `amazon-cognito-identity-js` client — the same library the Angular app uses.
//
// This single pool gates the ENTIRE back-office app (Support Console + SaaS
// Admin). The resulting **ID token** is also attached as
// `Authorization: Bearer <token>` on the SaaS-Admin feature-config API, which
// sits behind the Lambda's JWT check. Editing feature-config (PATCH) further
// requires the `saas-admin` Cognito group — enforced by the backend (403).
//
// Configuration comes from the environment (no secrets in code):
//   REACT_APP_COGNITO_USER_POOL_ID   e.g. ap-south-1_XXXXXXXXX
//   REACT_APP_COGNITO_CLIENT_ID      the App Client ID
// The AWS region is inferred from the User Pool ID prefix.
// =============================================================================
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';

const USER_POOL_ID = (process.env.REACT_APP_COGNITO_USER_POOL_ID || '').trim();
const CLIENT_ID = (process.env.REACT_APP_COGNITO_CLIENT_ID || '').trim();

/** True only when both Cognito identifiers are present in the environment. */
export function isConfigured(): boolean {
  return !!(USER_POOL_ID && CLIENT_ID);
}

let pool: CognitoUserPool | null = null;
function getPool(): CognitoUserPool {
  if (!isConfigured()) {
    throw new Error(
      'Cognito is not configured. Set REACT_APP_COGNITO_USER_POOL_ID and ' +
      'REACT_APP_COGNITO_CLIENT_ID in the .env file.',
    );
  }
  if (!pool) {
    pool = new CognitoUserPool({ UserPoolId: USER_POOL_ID, ClientId: CLIENT_ID });
  }
  return pool;
}

/** The signed-in user, distilled from the Cognito ID-token claims. */
export interface AuthPrincipal {
  sub: string;
  username: string;
  email: string;
  name: string;
  groups: string[];
}

export type SignInResult =
  | { status: 'ok'; principal: AuthPrincipal; idToken: string }
  | { status: 'newPasswordRequired'; user: CognitoUser };

function principalFromSession(session: CognitoUserSession): AuthPrincipal {
  const claims = session.getIdToken().decodePayload() as Record<string, any>;
  const groups = claims['cognito:groups'];
  return {
    sub: claims.sub,
    username: claims['cognito:username'] || claims.username || claims.email || claims.sub,
    email: claims.email || '',
    name: claims.name || claims['cognito:username'] || claims.email || 'User',
    groups: Array.isArray(groups) ? groups : groups ? [String(groups)] : [],
  };
}

/**
 * Authenticate with an email + password. Resolves with either a ready session
 * (`ok`) or a `newPasswordRequired` challenge for first-time users who still
 * hold a temporary password.
 */
export function signIn(username: string, password: string): Promise<SignInResult> {
  const user = new CognitoUser({ Username: username, Pool: getPool() });
  const authDetails = new AuthenticationDetails({ Username: username, Password: password });

  return new Promise<SignInResult>((resolve, reject) => {
    user.authenticateUser(authDetails, {
      onSuccess: (session) =>
        resolve({
          status: 'ok',
          principal: principalFromSession(session),
          idToken: session.getIdToken().getJwtToken(),
        }),
      onFailure: (err) => reject(err),
      newPasswordRequired: () => resolve({ status: 'newPasswordRequired', user }),
    });
  });
}

/**
 * Complete the NEW_PASSWORD_REQUIRED challenge for a first-time login. The
 * `user` comes from the `signIn` result. No profile attributes are changed.
 */
export function completeNewPassword(user: CognitoUser, newPassword: string): Promise<SignInResult> {
  return new Promise<SignInResult>((resolve, reject) => {
    user.completeNewPasswordChallenge(newPassword, {}, {
      onSuccess: (session) =>
        resolve({
          status: 'ok',
          principal: principalFromSession(session),
          idToken: session.getIdToken().getJwtToken(),
        }),
      onFailure: (err) => reject(err),
    });
  });
}

/**
 * Current valid ID token for the last-authenticated user, or null if there is
 * no session (or it can't be refreshed). `getSession` silently refreshes an
 * expired ID/access token using the stored refresh token.
 */
export function getIdToken(): Promise<string | null> {
  if (!isConfigured()) return Promise.resolve(null);
  const user = getPool().getCurrentUser();
  if (!user) return Promise.resolve(null);

  return new Promise<string | null>((resolve) => {
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) {
        resolve(null);
        return;
      }
      resolve(session.getIdToken().getJwtToken());
    });
  });
}

/**
 * Current valid ACCESS token. The SignalR hub validates token_use=access, so
 * the id token above will not work there.
 */
export function getAccessToken(): Promise<string | null> {
  if (!isConfigured()) return Promise.resolve(null);
  const user = getPool().getCurrentUser();
  if (!user) return Promise.resolve(null);

  return new Promise<string | null>((resolve) => {
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) {
        resolve(null);
        return;
      }
      resolve(session.getAccessToken().getJwtToken());
    });
  });
}

/** Re-hydrate the principal from a persisted session (e.g. after reload). */
export function getCurrentPrincipal(): Promise<AuthPrincipal | null> {
  if (!isConfigured()) return Promise.resolve(null);
  const user = getPool().getCurrentUser();
  if (!user) return Promise.resolve(null);

  return new Promise<AuthPrincipal | null>((resolve) => {
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) {
        resolve(null);
        return;
      }
      resolve(principalFromSession(session));
    });
  });
}

/** Clear the local Cognito session (tokens in localStorage). */
export function signOut(): void {
  if (!isConfigured()) return;
  const user = getPool().getCurrentUser();
  if (user) user.signOut();
}

/** Human-readable message for common Cognito error codes. */
export function friendlyAuthError(err: any): string {
  const code = err?.code || err?.name || '';
  switch (code) {
    case 'NotAuthorizedException':
      return 'Incorrect email or password.';
    case 'UserNotFoundException':
      return 'No account found for that email.';
    case 'UserNotConfirmedException':
      return 'This account is not confirmed yet. Contact your administrator.';
    case 'PasswordResetRequiredException':
      return 'A password reset is required. Contact your administrator.';
    case 'InvalidPasswordException':
      return err?.message || 'Password does not meet the required policy.';
    case 'TooManyRequestsException':
    case 'LimitExceededException':
      return 'Too many attempts. Please wait a moment and try again.';
    default:
      return err?.message || 'Sign-in failed. Please try again.';
  }
}
